/**
 * Manages site navigation, breadcrumbs, and dynamic CMS-driven routes.
 *
 * Navigation data is built from two sources:
 * - Static routes from Vue Router (file-based routing in /app/pages/)
 * - Dynamic routes from Directus CMS (industries, tradeshows)
 *
 * The nav state is stored as both a flat list and a keyed index for fast lookups.
 * Data is cached via Nuxt's payload extraction to avoid refetching on client hydration.
 */
export function useNav() {
  const route = useRoute()
  const router = useRouter()

  interface TheNav {
    list: NavItem[]
    index: Record<NavItem['name'], NavItem>
  }

  // ==================
  // State
  // ==================

  const { data: theNav } = useAsyncData<TheNav>(
    'cpos:nav:the-nav',
    async () => buildNavItems(),
    {
      getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
    },
  )

  // ==================
  // Computed
  // ==================

  /** Top-level menu items, excluding index and dev pages, sorted by order */
  const menu = computed(() => theNav.value?.list.filter(item => item.name !== 'index' && item.name !== 'rand').filter(item => splitPath(item.to).length === 1).sort((a, b) => (a.order ?? 99) - (b.order ?? 100)))

  /** Current path split into array of slugs (e.g., '/about/stories' -> ['about', 'stories']) */
  const pathArray = computed(() => splitPath(route.path))

  /** Breadcrumb links derived from current path, used by TheBreadcrumbs component */
  const breadcrumbs = computed(() => {
    const breadcrumbArray = pathArray.value.map((slug, i) => {
      const name = `${pathArray.value.slice(0, i + 1).join('-')}`
      const crumb = getNavItem(name)
      return {
        title: crumb?.label ?? crumb?.title ?? crumb?.name ?? slug.replaceAll('-', ' '),
        to: `/${pathArray.value.slice(0, i + 1).join('/')}`,
      }
    })
    return breadcrumbArray
  })

  /** Current page's NavItem, looked up by route name */
  const location = computed(() => getNavItem(route.name as string))

  // ==================
  // Actions
  // ==================

  /** Looks up a single NavItem by its name key */
  function getNavItem(itemName: NavItem['name']) {
    return theNav.value?.index[itemName]
  }

  /** Looks up multiple NavItems by name, filtering out missing entries and sorting by order */
  function getNavItems(itemNames: NavItem['name'][]) {
    return itemNames.map((name) => {
      if (!theNav.value?.index[name])
        return null
      else
        return theNav.value?.index[name]
    }).filter((item): item is NavItem => !!item).sort((a, b) => (a.order ?? 99) - (b.order ?? 100))
  }

  /**
   * Builds the complete navigation tree from Vue Router routes and Directus CMS data.
   * Static routes are extracted from the router, then dynamic items (industries)
   * and external links (Trexon, Careers) are fetched and injected as children
   * of their parent nav items.
   */
  async function buildNavItems() {
    const theRoutes = router.getRoutes()
    const list: NavItem[] = []
    const index: Record<NavItem['to'], NavItem> = {}

    // Convert static routes to NavItems, skipping dynamic params and redirects
    theRoutes.forEach((route) => {
      if (route.path.includes(':'))
        return
      if (route.name === undefined)
        return

      const navItem = transformRouteToNavItem(route)

      list.push(navItem)

      index[navItem.name] = navItem
    })

    // Fetch dynamic industry pages from Directus CMS
    const industries: NavItem[] = (await $fetch<Industry[]>('/api/items/industries')).map(industry => ({
      to: `/industries/${industry.slug}`,
      name: `industries-${industry.slug}`,
      label: industry.name,
      order: industry.sort as number,
    }))

    // External links that appear in navigation dropdowns
    const trexonFamily = {
      to: 'https://www.trexon.com/connected-by-trexon',
      name: 'about-trexon-family',
      title: 'Connected by Trexon',
      exit: true,
      order: 3,
    }

    const careers = {
      to: 'https://job-boards.greenhouse.io/tpcengineeringholdingsllc?offices%5B%5D=4034829007',
      name: 'careers',
      title: 'Careers',
      exit: true,
      order: 4,
    }

    const contactTrexon = {
      to: 'https://www.trexon.com/contact',
      name: 'contact-trexon',
      title: 'Contact Trexon',
      exit: true,
      order: 1,
    }

    list.push(trexonFamily, careers, contactTrexon, ...industries)

    // Inject dynamic and external items as children of their parent nav items
    const aboutItem = list.find(item => item.name === 'about')
    const industriesItem = list.find(item => item.name === 'industries')
    const contactItem = list.find(item => item.name === 'contact')

    if (aboutItem?.children)
      aboutItem.children?.push(trexonFamily.name, careers.name)

    else if (aboutItem)
      aboutItem.children = [trexonFamily.name, careers.name]

    if (industriesItem?.children)
      industriesItem.children?.push(...industries.map(item => item.name))

    else if (industriesItem)
      industriesItem.children = industries.map(item => item.name)

    if (contactItem?.children)
      contactItem.children?.push(contactTrexon.name)

    else if (contactItem)
      contactItem.children = [contactTrexon.name]

    // Add dynamic and external items to the index for fast lookup
    index[trexonFamily.name] = trexonFamily
    index[careers.name] = careers
    index[contactTrexon.name] = contactTrexon
    industries.forEach((item) => {
      index[item.name] = item
    },

    )
    return {
      list,
      index,
    }
  }

  return {
    route,
    theNav,
    menu,
    pathArray,
    breadcrumbs,
    location,
    getNavItem,
    getNavItems,
  }
}
